import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { Calendar, ChefHat, UtensilsCrossed, ArrowRight, Clock, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function ResidentHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    API.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
    API.get('/events').then(r => setEvents(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="fade-in space-y-10" data-testid="resident-dashboard">
      {/* Welcome */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#222] font-[Manrope]">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="mt-2 text-[#717171] text-lg">What are you in the mood for today?</p>
      </div>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { to: '/meal-plans/new', icon: UtensilsCrossed, label: 'Start Meal Plan', desc: 'Customized weekly prep', color: 'bg-orange-50 text-[#E05A47]' },
          { to: '/in-home-booking', icon: ChefHat, label: 'Book Private Chef', desc: 'Date nights & dinner parties', color: 'bg-blue-50 text-blue-600' },
          { to: '/events', icon: Calendar, label: 'Browse Events', desc: 'Community dining experiences', color: 'bg-green-50 text-green-600' },
        ].map(({ to, icon: Icon, label, desc, color }) => (
          <Link
            key={to}
            to={to}
            data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
            className="bg-white rounded-2xl border border-[#EBEBEB] p-6 card-hover flex flex-col"
          >
            <div className={`w-11 h-11 rounded-xl ${color} flex items-center justify-center mb-4`}>
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <h3 className="font-bold text-[#222] font-[Manrope]">{label}</h3>
            <p className="text-sm text-[#717171] mt-1">{desc}</p>
            <ArrowRight className="w-4 h-4 text-[#717171] mt-auto pt-3" />
          </Link>
        ))}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Meal Plans', value: stats.my_meal_plans || 0 },
            { label: 'Bookings', value: stats.my_bookings || 0 },
            { label: 'Event Tickets', value: stats.my_event_bookings || 0 },
            { label: 'Available Events', value: stats.upcoming_events || 0 },
          ].map(({ label, value }) => (
            <div key={label} className="bg-[#F7F7F7] rounded-xl p-4 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              <p className="text-2xl font-extrabold text-[#222] font-[Manrope]">{value}</p>
              <p className="text-xs text-[#717171] mt-1 font-medium">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Upcoming Events */}
      {events.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold text-[#222] font-[Manrope]">Upcoming Events</h2>
            <Link to="/events" className="text-sm text-[#E05A47] font-semibold flex items-center gap-1 hover:underline">
              See all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {events.map(ev => (
              <Link key={ev.id} to={`/events/${ev.id}`} className="bg-white rounded-2xl border border-[#EBEBEB] overflow-hidden card-hover" data-testid={`event-card-${ev.id}`}>
                <img src={ev.image || 'https://images.unsplash.com/photo-1768508950719-4d76978fdf44'} alt={ev.title} className="w-full h-40 object-cover" />
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-[#717171] mb-2">
                    <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span>{new Date(ev.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · {ev.time}</span>
                  </div>
                  <h3 className="font-bold text-[#222] font-[Manrope]">{ev.title}</h3>
                  <div className="flex items-center gap-2 text-xs text-[#717171] mt-2">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span>{ev.location}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#EBEBEB]">
                    <span className="font-bold text-[#222] font-[Manrope]">${ev.price}</span>
                    <span className="text-xs text-[#717171]">{ev.capacity - (ev.seats_booked || 0)} seats left</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
